import {
  AllergyItemWriteSchema,
  FamilyHistoryRowWriteSchema,
  ImmunizationItemWriteSchema,
  MedicationItemWriteSchema,
  PmhItemWriteSchema,
  SocialHistoryItemWriteSchema,
} from "@emr/contracts";
import { type ReactNode, useEffect, useId, useLayoutEffect, useMemo, useState } from "react";
import { z } from "zod";
import type {
  AllergyItem,
  AllergyItemWrite,
  FamilyHistoryRow,
  FamilyHistoryRowWrite,
  FamilyHistorySection,
  FamilyHistorySectionWrite,
  ImmunizationItem,
  ImmunizationItemWrite,
  MedicationItem,
  MedicationItemWrite,
  PmhItem,
  PmhItemWrite,
  SocialHistoryItem,
  SocialHistoryItemWrite,
} from "../../api/clinicalWorkspace";
import { FieldInput } from "./FieldInput";
import { type EditorIssue, SectionEditorFrame } from "./SectionEditorFrame";
import {
  draftFromItem,
  emptyDraft,
  type FieldOption,
  type FieldSpec,
  type FieldValue,
  fieldBoolean,
  fieldString,
  optionalNumber,
  optionalText,
  type RowDraft,
  rowIdentity,
  useRowDrafts,
} from "./useRowDrafts";

/**
 * The list-shaped section editors. Each section is one configuration —
 * field specs, item ⇄ draft mapping, the write schema that validates a row —
 * over a single generic editor, so the six sections share one behaviour:
 * rows edit in place, a new row appends, Remove drops it (the server
 * inactivates the head), a quick action such as Stop or Inactivate flips the
 * status without hunting for the select, and Save validates every row
 * against the contract before anything leaves the browser.
 */

export interface ListEditorProps<TItem, TWrite> {
  readonly items: readonly TItem[];
  readonly startWithNewRow?: boolean | undefined;
  /** Patient + section + edit session; rotating it resets the drafts. */
  readonly resetKey: string;
  readonly saving: boolean;
  readonly serverError: string | null;
  readonly onSave: (items: TWrite[]) => void;
  readonly onCancel: () => void;
  readonly onReload?: (() => void) | undefined;
  readonly onDirtyChange?: ((dirty: boolean) => void) | undefined;
}

interface ListEditorConfig<TItem, TWrite> {
  readonly label: string;
  readonly singular: string;
  readonly specs: readonly FieldSpec[];
  readonly schema: z.ZodType<TWrite>;
  readonly fromItem: (item: TItem) => RowDraft;
  readonly toItem: (row: RowDraft) => Record<string, unknown>;
  readonly rowTitle: (row: RowDraft) => string;
  readonly emptyHint: string;
  readonly quickActions?: (
    row: RowDraft,
    update: (key: string, value: FieldValue) => void,
  ) => ReactNode;
  /** Checks a row's write shape cannot express (they need the draft's hidden values). */
  readonly rowIssues?: (row: RowDraft) => readonly RowIssue[];
  /** The closed disclosure's summary for a row; defaults to the specs' disclosure label. */
  readonly disclosureSummary?: (row: RowDraft, label: string) => string;
}

interface RowIssue {
  readonly key: string;
  readonly message: string;
}

interface PathIssue {
  readonly path: readonly PropertyKey[];
  readonly message: string;
}

function option(value: string, label: string) {
  return { value, label };
}

function issueSummary(
  config: { readonly specs: readonly FieldSpec[]; readonly rowTitle: (row: RowDraft) => string },
  rows: readonly RowDraft[],
  idPrefix: string,
  found: readonly PathIssue[],
): { issues: EditorIssue[]; fieldErrors: Record<string, string> } {
  const issues: EditorIssue[] = [];
  const fieldErrors: Record<string, string> = {};
  for (const issue of found) {
    const [rowIndex, fieldName] = issue.path;
    const row = typeof rowIndex === "number" ? rows[rowIndex] : undefined;
    const field = typeof fieldName === "string" ? fieldName : undefined;
    const spec = config.specs.find((candidate) => candidate.key === field);
    const rowLabel =
      row === undefined ? "Section" : config.rowTitle(row) || `Row ${(rowIndex as number) + 1}`;
    const message = `${rowLabel}: ${spec?.label ?? field ?? "value"} — ${issue.message}`;
    const inputId =
      row !== undefined && field !== undefined && spec !== undefined
        ? `${idPrefix}-${row.clientKey}-${field}`
        : undefined;
    if (row !== undefined && field !== undefined) {
      fieldErrors[`${row.clientKey}.${field}`] = issue.message;
    }
    issues.push({
      key: `${String(rowIndex ?? "section")}.${field ?? ""}.${issue.message}`,
      message,
      ...(inputId === undefined ? {} : { inputId }),
    });
  }
  return { issues, fieldErrors };
}

export function ListSectionEditor<TItem, TWrite>({
  config,
  items,
  resetKey,
  saving,
  serverError,
  onSave,
  onCancel,
  onReload,
  onDirtyChange,
  before,
  extraDirty = false,
  addControl,
  startWithNewRow = false,
}: ListEditorProps<TItem, TWrite> & {
  readonly config: ListEditorConfig<TItem, TWrite>;
  /** Section-level controls rendered above the rows (the family narrative). */
  readonly before?: ReactNode;
  readonly extraDirty?: boolean;
  /** Custom add control (social history picks a kind); default is one button. */
  readonly addControl?: (add: (row: RowDraft) => void) => ReactNode;
}) {
  const idPrefix = `pp-${resetKey.replace(/[^a-z0-9]+/gi, "-")}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: drafts belong to the edit session; a refetch must not rebuild them.
  const initial = useMemo(
    () => [...items.map(config.fromItem), ...(startWithNewRow ? [emptyDraft(config.specs)] : [])],
    [resetKey],
  );
  const drafts = useRowDrafts({ initial, resetKey, toItem: config.toItem });
  const [issues, setIssues] = useState<EditorIssue[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const dirty = drafts.dirty || extraDirty;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const submit = () => {
    const parsed = z.array(config.schema).safeParse(drafts.rows.map(config.toItem));
    const rowIssues: PathIssue[] = drafts.rows.flatMap((row, index) =>
      (config.rowIssues?.(row) ?? []).map((issue) => ({
        path: [index, issue.key],
        message: issue.message,
      })),
    );
    if (!parsed.success || rowIssues.length > 0) {
      const summary = issueSummary(config, drafts.rows, idPrefix, [
        ...(parsed.success ? [] : parsed.error.issues),
        ...rowIssues,
      ]);
      setIssues(summary.issues);
      setFieldErrors(summary.fieldErrors);
      return;
    }
    setIssues([]);
    setFieldErrors({});
    onSave(parsed.data);
  };

  const addRow = () => drafts.add(emptyDraft(config.specs));

  return (
    <SectionEditorFrame
      label={config.label}
      issues={issues}
      serverError={serverError}
      saving={saving}
      dirty={dirty}
      onSubmit={submit}
      onCancel={onCancel}
      onReload={onReload}
    >
      {before}
      {drafts.rows.length === 0 ? (
        <p className="pp-quiet">{config.emptyHint}</p>
      ) : (
        <ul className="pp-editor-rows">
          {drafts.rows.map((row) => {
            const title = config.rowTitle(row) || `New ${config.singular}`;
            const update = (key: string, value: FieldValue) => {
              drafts.update(row.clientKey, key, value);
              setFieldErrors((current) => {
                if (current[`${row.clientKey}.${key}`] === undefined) return current;
                const next = { ...current };
                delete next[`${row.clientKey}.${key}`];
                return next;
              });
            };
            return (
              <li key={row.clientKey} className="pp-editor-row">
                <div className="pp-editor-row-head">
                  <span className="pp-editor-row-title">{title}</span>
                  <div className="pp-inline-actions">
                    {config.quickActions?.(row, update)}
                    <button
                      type="button"
                      className="pp-btn ghost sm"
                      aria-label={`Remove ${title}`}
                      onClick={() => drafts.remove(row.clientKey)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div className="pp-editor-fields">
                  {config.specs
                    .filter((spec) => spec.disclosure === undefined)
                    .map((spec) => (
                      <FieldInput
                        key={spec.key}
                        id={`${idPrefix}-${row.clientKey}-${spec.key}`}
                        spec={spec}
                        value={row.fields[spec.key]}
                        error={fieldErrors[`${row.clientKey}.${spec.key}`]}
                        onChange={(value) => update(spec.key, value)}
                      />
                    ))}
                </div>
                {disclosureGroups(config.specs).map(([label, specs]) => (
                  <FieldDisclosure
                    key={label}
                    summary={config.disclosureSummary?.(row, label) ?? label}
                    hasError={specs.some(
                      (spec) => fieldErrors[`${row.clientKey}.${spec.key}`] !== undefined,
                    )}
                  >
                    <div className="pp-editor-fields">
                      {specs.map((spec) => (
                        <FieldInput
                          key={spec.key}
                          id={`${idPrefix}-${row.clientKey}-${spec.key}`}
                          spec={spec}
                          value={row.fields[spec.key]}
                          error={fieldErrors[`${row.clientKey}.${spec.key}`]}
                          onChange={(value) => update(spec.key, value)}
                        />
                      ))}
                    </div>
                  </FieldDisclosure>
                ))}
              </li>
            );
          })}
        </ul>
      )}
      <div className="pp-editor-add">
        {addControl ? (
          addControl(drafts.add)
        ) : (
          <button type="button" className="pp-btn ghost sm" onClick={addRow}>
            Add {config.singular}
          </button>
        )}
      </div>
    </SectionEditorFrame>
  );
}

/** Disclosure specs grouped by label, in spec order. */
function disclosureGroups(specs: readonly FieldSpec[]): [string, FieldSpec[]][] {
  const groups = new Map<string, FieldSpec[]>();
  for (const spec of specs) {
    if (spec.disclosure === undefined) continue;
    groups.set(spec.disclosure, [...(groups.get(spec.disclosure) ?? []), spec]);
  }
  return [...groups];
}

/**
 * A closed-by-default group of optional fields. It opens itself when one of
 * its fields fails validation, so the summary's jump lands on a visible input,
 * and stays open while the clinician fixes it.
 */
function FieldDisclosure({
  summary,
  hasError,
  children,
}: {
  readonly summary: string;
  readonly hasError: boolean;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(hasError);
  useEffect(() => {
    if (hasError) setOpen(true);
  }, [hasError]);
  return (
    <details
      className="pp-editor-disclosure"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>{summary}</summary>
      {children}
    </details>
  );
}

/* ---- Allergy and medication codes -------------------------------------- */

/**
 * The optional interoperability code on an allergy or medication (PC-4). Both
 * fields sit in a closed "Code" disclosure. A new code needs its system; a
 * stored code left as it was may have none (it predates coding systems) and
 * the writer keeps whatever system it has.
 */
function codeSpecs(systems: readonly FieldOption[]): FieldSpec[] {
  return [
    { key: "code", label: "Code", kind: "text", narrow: true, disclosure: "Code" },
    {
      key: "codeSystem",
      label: "Code system",
      kind: "select",
      narrow: true,
      disclosure: "Code",
      options: [option("", "—"), ...systems],
    },
  ];
}

function codeFields(row: RowDraft): { code?: string; codeSystem?: string } {
  const code = fieldString(row, "code");
  const codeSystem = fieldString(row, "codeSystem");
  if (code.length === 0) return {};
  return codeSystem.length === 0 ? { code } : { code, codeSystem };
}

/** An existing row whose code changed must name the new code's system. */
function changedCodeNeedsSystem(row: RowDraft): readonly RowIssue[] {
  const code = fieldString(row, "code");
  return row.id !== undefined &&
    code.length > 0 &&
    fieldString(row, "codeSystem").length === 0 &&
    code !== fieldString(row, "codeOriginal")
    ? [{ key: "codeSystem", message: "Choose the system for this code" }]
    : [];
}

function codeSummary(systems: readonly FieldOption[]) {
  return (row: RowDraft, label: string) => {
    const code = fieldString(row, "code");
    if (code.length === 0) return `${label} (optional)`;
    const system = systems.find((candidate) => candidate.value === fieldString(row, "codeSystem"));
    return system === undefined ? `${label}: ${code}` : `${label}: ${code} · ${system.label}`;
  };
}

/* ---- Past medical history ---------------------------------------------- */

const CURRENT_YEAR = new Date().getFullYear();

const PMH_SPECS: readonly FieldSpec[] = [
  { key: "display", label: "Condition or procedure", kind: "text", required: true, wide: true },
  {
    key: "onsetYear",
    label: "Since (year)",
    kind: "number",
    narrow: true,
    min: 1900,
    max: CURRENT_YEAR,
  },
  {
    key: "status",
    label: "Status",
    kind: "select",
    narrow: true,
    options: [option("active", "Active"), option("resolved", "Resolved")],
  },
  { key: "surgical", label: "Surgical or procedure", kind: "checkbox" },
  { key: "code", label: "Code", kind: "text", narrow: true, placeholder: "Optional" },
  {
    key: "codeSystem",
    label: "Code system",
    kind: "select",
    narrow: true,
    options: [
      option("", "—"),
      option("ICD-10-CM", "ICD-10-CM"),
      option("ICD-10-CA", "ICD-10-CA"),
      option("SNOMED-CT", "SNOMED CT"),
      option("local", "Local"),
    ],
  },
  { key: "note", label: "Note", kind: "textarea", wide: true },
];

const pmhConfig: ListEditorConfig<PmhItem, PmhItemWrite> = {
  label: "Past medical history",
  singular: "condition",
  specs: PMH_SPECS,
  schema: PmhItemWriteSchema,
  emptyHint: "Nothing recorded yet. Add the conditions and procedures that matter to this chart.",
  fromItem: (item) =>
    draftFromItem(PMH_SPECS, item, {
      onsetYear: item.onsetDate?.slice(0, 4) ?? "",
      onsetDateOriginal: item.onsetDate ?? "",
      status: item.status === "resolved" ? "resolved" : "active",
      surgical: item.category === "surgical",
      sourceKind: item.sourceKind ?? "",
    }),
  toItem: (row) => {
    const year = fieldString(row, "onsetYear");
    const original = fieldString(row, "onsetDateOriginal");
    // The year is what the clinician edits; a full original date survives an
    // unchanged year so an untouched row never supersedes itself.
    const onsetDate =
      year.length === 0 ? undefined : original.startsWith(year) ? original : `${year}-01-01`;
    const code = fieldString(row, "code");
    return {
      ...rowIdentity(row),
      display: fieldString(row, "display"),
      status: fieldString(row, "status") || "active",
      category: fieldBoolean(row, "surgical") ? "surgical" : "condition",
      ...(onsetDate === undefined ? {} : { onsetDate }),
      ...(code.length === 0
        ? {}
        : { code, codeSystem: fieldString(row, "codeSystem") || "ICD-10-CM" }),
      ...optionalText(row, "note"),
      ...optionalText(row, "sourceKind"),
    };
  },
  rowTitle: (row) => fieldString(row, "display"),
};

export function PmhEditor(props: ListEditorProps<PmhItem, PmhItemWrite>) {
  return <ListSectionEditor config={pmhConfig} {...props} />;
}

/* ---- Allergies ---------------------------------------------------------- */

const ALLERGY_CODE_SYSTEMS: readonly FieldOption[] = [
  option("SNOMED-CT", "SNOMED CT"),
  option("RxNorm", "RxNorm"),
  option("DIN", "DIN"),
  option("FDB_HIC_SEQN", "FDB ingredient"),
  option("FDB_DAM_ALRGN_GRP", "FDB allergen group"),
  option("local", "Local"),
];

const ALLERGY_SPECS: readonly FieldSpec[] = [
  { key: "display", label: "Substance", kind: "text", required: true, wide: true },
  { key: "reaction", label: "Reaction", kind: "text", wide: true },
  {
    key: "severity",
    label: "Severity",
    kind: "select",
    narrow: true,
    options: [
      option("", "—"),
      option("mild", "Mild"),
      option("moderate", "Moderate"),
      option("severe", "Severe"),
      option("unknown", "Unknown"),
    ],
  },
  {
    key: "status",
    label: "Status",
    kind: "select",
    narrow: true,
    options: [
      option("active", "Active"),
      option("inactive", "Inactive"),
      option("resolved", "Resolved"),
    ],
  },
  ...codeSpecs(ALLERGY_CODE_SYSTEMS),
];

const allergyConfig: ListEditorConfig<AllergyItem, AllergyItemWrite> = {
  label: "Allergies",
  singular: "allergy",
  specs: ALLERGY_SPECS,
  schema: AllergyItemWriteSchema,
  emptyHint: "No known allergies recorded.",
  // codeOriginal tells an untouched stored code (which may predate coding
  // systems, 20260925123232) from one the clinician changed.
  fromItem: (item) =>
    draftFromItem(ALLERGY_SPECS, item, {
      codeOriginal: item.code ?? "",
      sourceKind: item.sourceKind ?? "",
    }),
  toItem: (row) => ({
    ...rowIdentity(row),
    display: fieldString(row, "display"),
    status: fieldString(row, "status") || "active",
    ...codeFields(row),
    ...optionalText(row, "reaction"),
    ...optionalText(row, "severity"),
    ...optionalText(row, "sourceKind"),
  }),
  rowTitle: (row) => fieldString(row, "display"),
  rowIssues: changedCodeNeedsSystem,
  disclosureSummary: codeSummary(ALLERGY_CODE_SYSTEMS),
  quickActions: (row, update) =>
    fieldString(row, "status") === "active" ? (
      <button
        type="button"
        className="pp-btn ghost sm"
        onClick={() => update("status", "inactive")}
        aria-label={`Inactivate ${fieldString(row, "display") || "allergy"}`}
      >
        Inactivate
      </button>
    ) : null,
};

export function AllergiesEditor(props: ListEditorProps<AllergyItem, AllergyItemWrite>) {
  return <ListSectionEditor config={allergyConfig} {...props} />;
}

/* ---- Medications -------------------------------------------------------- */

const MEDICATION_CODE_SYSTEMS: readonly FieldOption[] = [
  option("DIN", "DIN"),
  option("RxNorm", "RxNorm"),
  option("FDB_MEDID", "FDB MEDID"),
  option("local", "Local"),
];

const MEDICATION_SPECS: readonly FieldSpec[] = [
  { key: "display", label: "Medication", kind: "text", required: true, wide: true },
  { key: "sig", label: "Directions", kind: "text", wide: true },
  {
    key: "status",
    label: "Status",
    kind: "select",
    narrow: true,
    options: [
      option("active", "Active"),
      option("inactive", "Stopped"),
      option("completed", "Completed"),
      option("unknown", "Unknown"),
    ],
  },
  {
    key: "sourceKind",
    label: "Source",
    kind: "select",
    narrow: true,
    options: [
      option("", "—"),
      option("clinician_prescribed", "Prescribed here"),
      option("clinician_asserted", "Recorded here"),
      option("patient_reported", "Patient-reported"),
      option("external_document", "Imported"),
      option("medication_reconciliation", "Reconciled"),
    ],
  },
  ...codeSpecs(MEDICATION_CODE_SYSTEMS),
];

const medicationConfig: ListEditorConfig<MedicationItem, MedicationItemWrite> = {
  label: "Medications",
  singular: "medication",
  specs: MEDICATION_SPECS,
  schema: MedicationItemWriteSchema,
  emptyHint: "No medications recorded.",
  fromItem: (item) => draftFromItem(MEDICATION_SPECS, item, { codeOriginal: item.code ?? "" }),
  toItem: (row) => ({
    ...rowIdentity(row),
    display: fieldString(row, "display"),
    status: fieldString(row, "status") || "active",
    ...codeFields(row),
    ...optionalText(row, "sig"),
    ...optionalText(row, "sourceKind"),
  }),
  rowTitle: (row) => fieldString(row, "display"),
  rowIssues: changedCodeNeedsSystem,
  disclosureSummary: codeSummary(MEDICATION_CODE_SYSTEMS),
  quickActions: (row, update) =>
    fieldString(row, "status") === "active" ? (
      <button
        type="button"
        className="pp-btn ghost sm"
        onClick={() => update("status", "inactive")}
        aria-label={`Stop ${fieldString(row, "display") || "medication"}`}
      >
        Stop
      </button>
    ) : null,
};

export function MedicationsEditor(props: ListEditorProps<MedicationItem, MedicationItemWrite>) {
  return <ListSectionEditor config={medicationConfig} {...props} />;
}

/* ---- Immunizations ------------------------------------------------------ */

const IMMUNIZATION_SPECS: readonly FieldSpec[] = [
  { key: "display", label: "Vaccine", kind: "text", required: true, wide: true },
  { key: "administeredOn", label: "Given on", kind: "date", required: true, narrow: true },
  { key: "doseNumber", label: "Dose", kind: "number", narrow: true, min: 1, max: 20 },
  {
    key: "route",
    label: "Route",
    kind: "select",
    narrow: true,
    options: [
      option("", "—"),
      option("IM", "Intramuscular"),
      option("SC", "Subcutaneous"),
      option("ID", "Intradermal"),
      option("oral", "Oral"),
      option("intranasal", "Intranasal"),
      option("other", "Other"),
    ],
  },
  { key: "site", label: "Site", kind: "text", narrow: true },
  { key: "lotNumber", label: "Lot", kind: "text", narrow: true },
  {
    key: "status",
    label: "Status",
    kind: "select",
    narrow: true,
    options: [option("completed", "Completed"), option("not_done", "Not done")],
  },
  { key: "note", label: "Note", kind: "textarea", wide: true },
];

const immunizationConfig: ListEditorConfig<ImmunizationItem, ImmunizationItemWrite> = {
  label: "Immunizations",
  singular: "immunization",
  specs: IMMUNIZATION_SPECS,
  schema: ImmunizationItemWriteSchema,
  emptyHint: "Nothing recorded yet.",
  fromItem: (item) =>
    draftFromItem(IMMUNIZATION_SPECS, item, {
      code: item.code ?? "",
      codeSystem: item.codeSystem ?? "",
      sourceKind: item.sourceKind ?? "",
    }),
  toItem: (row) => {
    const code = fieldString(row, "code");
    return {
      ...rowIdentity(row),
      display: fieldString(row, "display"),
      administeredOn: fieldString(row, "administeredOn"),
      status: fieldString(row, "status") || "completed",
      ...optionalNumber(row, "doseNumber"),
      ...optionalText(row, "route"),
      ...optionalText(row, "site"),
      ...optionalText(row, "lotNumber"),
      ...optionalText(row, "note"),
      ...(code.length === 0 ? {} : { code, codeSystem: fieldString(row, "codeSystem") || "CVX" }),
      ...optionalText(row, "sourceKind"),
    };
  },
  rowTitle: (row) => fieldString(row, "display"),
};

export function ImmunizationsEditor(
  props: ListEditorProps<ImmunizationItem, ImmunizationItemWrite>,
) {
  return <ListSectionEditor config={immunizationConfig} {...props} />;
}

/* ---- Social history ----------------------------------------------------- */

export const SOCIAL_KIND_OPTIONS = [
  option("tobacco", "Tobacco"),
  option("alcohol", "Alcohol"),
  option("cannabis", "Cannabis"),
  option("substance", "Substances"),
  option("occupation", "Occupation"),
  option("housing", "Housing"),
  option("exercise", "Exercise"),
  option("diet", "Diet"),
  option("other", "Other"),
] as const;

const SOCIAL_SPECS: readonly FieldSpec[] = [
  { key: "kind", label: "Kind", kind: "select", narrow: true, options: SOCIAL_KIND_OPTIONS },
  {
    key: "status",
    label: "Status",
    kind: "select",
    narrow: true,
    options: [
      option("current", "Current"),
      option("former", "Former"),
      option("never", "Never"),
      option("unknown", "Unknown"),
    ],
  },
  { key: "detail", label: "Detail", kind: "text", required: true, wide: true },
  { key: "quantity", label: "Quantity", kind: "text", narrow: true },
  { key: "frequency", label: "Frequency", kind: "text", narrow: true },
  { key: "note", label: "Note", kind: "textarea", wide: true },
];

function socialKindLabel(kind: string): string {
  return SOCIAL_KIND_OPTIONS.find((candidate) => candidate.value === kind)?.label ?? kind;
}

const socialConfig: ListEditorConfig<SocialHistoryItem, SocialHistoryItemWrite> = {
  label: "Social history",
  singular: "entry",
  specs: SOCIAL_SPECS,
  schema: SocialHistoryItemWriteSchema,
  emptyHint: "Nothing captured yet. Add only the kinds that are known.",
  fromItem: (item) => draftFromItem(SOCIAL_SPECS, item, { sourceKind: item.sourceKind ?? "" }),
  toItem: (row) => ({
    ...rowIdentity(row),
    kind: fieldString(row, "kind") || "other",
    status: fieldString(row, "status") || "unknown",
    detail: fieldString(row, "detail"),
    ...optionalText(row, "quantity"),
    ...optionalText(row, "frequency"),
    ...optionalText(row, "note"),
    ...optionalText(row, "sourceKind"),
  }),
  rowTitle: (row) => socialKindLabel(fieldString(row, "kind")),
};

export function SocialHistoryEditor(
  props: ListEditorProps<SocialHistoryItem, SocialHistoryItemWrite>,
) {
  const addKindId = useId();
  return (
    <ListSectionEditor
      config={socialConfig}
      {...props}
      addControl={(add) => (
        <label className="pp-field pp-field-narrow" htmlFor={addKindId}>
          <span>Add kind</span>
          <select
            id={addKindId}
            value=""
            onChange={(event) => {
              if (event.target.value.length === 0) return;
              add(emptyDraft(SOCIAL_SPECS, { kind: event.target.value, status: "current" }));
            }}
          >
            <option value="">Choose…</option>
            {SOCIAL_KIND_OPTIONS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </select>
        </label>
      )}
    />
  );
}

/* ---- Family history: narrative + rows ----------------------------------- */

const FAMILY_SPECS: readonly FieldSpec[] = [
  {
    key: "relation",
    label: "Relative",
    kind: "select",
    narrow: true,
    options: [
      option("mother", "Mother"),
      option("father", "Father"),
      option("sister", "Sister"),
      option("brother", "Brother"),
      option("daughter", "Daughter"),
      option("son", "Son"),
      option("maternal_grandmother", "Maternal grandmother"),
      option("maternal_grandfather", "Maternal grandfather"),
      option("paternal_grandmother", "Paternal grandmother"),
      option("paternal_grandfather", "Paternal grandfather"),
      option("aunt", "Aunt"),
      option("uncle", "Uncle"),
      option("cousin", "Cousin"),
      option("other", "Other relative"),
    ],
  },
  { key: "display", label: "Condition", kind: "text", required: true, wide: true },
  { key: "onsetAge", label: "From age", kind: "number", narrow: true, min: 0, max: 130 },
  { key: "deceased", label: "Deceased", kind: "checkbox" },
  { key: "note", label: "Note", kind: "textarea", wide: true },
];

const familyRowConfig: ListEditorConfig<FamilyHistoryRow, FamilyHistoryRowWrite> = {
  label: "Family history",
  singular: "relative",
  specs: FAMILY_SPECS,
  schema: FamilyHistoryRowWriteSchema,
  emptyHint: "No structured rows. The narrative above is enough on its own.",
  fromItem: (item) =>
    draftFromItem(FAMILY_SPECS, item, {
      deceasedOriginal: item.deceased === true,
      sourceKind: item.sourceKind ?? "",
    }),
  toItem: (row) => {
    const deceased = fieldBoolean(row, "deceased");
    const wasDeceased = fieldBoolean(row, "deceasedOriginal");
    return {
      ...rowIdentity(row),
      relation: fieldString(row, "relation") || "other",
      display: fieldString(row, "display"),
      ...optionalNumber(row, "onsetAge"),
      ...(deceased || wasDeceased ? { deceased } : {}),
      ...optionalText(row, "note"),
      ...optionalText(row, "sourceKind"),
    };
  },
  rowTitle: (row) => fieldString(row, "display"),
};

export function FamilyHistoryEditor({
  section,
  resetKey,
  saving,
  serverError,
  onSave,
  onCancel,
  onReload,
  onDirtyChange,
  startWithNewRow,
}: {
  readonly section: FamilyHistorySection;
  readonly resetKey: string;
  readonly saving: boolean;
  readonly serverError: string | null;
  readonly onSave: (section: FamilyHistorySectionWrite) => void;
  readonly onCancel: () => void;
  readonly onReload?: (() => void) | undefined;
  readonly onDirtyChange?: ((dirty: boolean) => void) | undefined;
  readonly startWithNewRow?: boolean | undefined;
}) {
  const [narrative, setNarrative] = useState(section.narrative);
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is the intentional trigger.
  useLayoutEffect(() => {
    setNarrative(section.narrative);
  }, [resetKey]);
  const narrativeDirty = narrative.trim() !== section.narrative.trim();
  const narrativeId = `pp-family-narrative-${resetKey.replace(/[^a-z0-9]+/gi, "-")}`;

  return (
    <ListSectionEditor
      config={familyRowConfig}
      startWithNewRow={startWithNewRow}
      items={section.rows.filter((row) => row.status === "active")}
      resetKey={resetKey}
      saving={saving}
      serverError={serverError}
      onCancel={onCancel}
      onReload={onReload}
      onDirtyChange={onDirtyChange}
      extraDirty={narrativeDirty}
      onSave={(rows) => onSave({ narrative: narrative.trim(), rows })}
      before={
        <label className="pp-field pp-field-wide" htmlFor={narrativeId}>
          <span>In your words</span>
          <textarea
            id={narrativeId}
            rows={4}
            value={narrative}
            maxLength={4000}
            placeholder="Mother: breast cancer at 52. Father: type 2 diabetes."
            onChange={(event) => setNarrative(event.target.value)}
          />
        </label>
      }
    />
  );
}
